import { Link } from "react-router-dom";
import { Users, Briefcase, Search, Building2, Handshake, Shield, CheckCircle2, ArrowRight } from "lucide-react";
import ScrollReveal from "@/components/ScrollReveal";

const servicesData = [
  {
    icon: Users,
    title: "Permanent Staffing",
    desc: "We help organizations find the right talent for long-term roles. Our rigorous screening process ensures candidates who align with your company culture, values, and growth trajectory.",
    benefits: ["Deep talent pool across industries", "Culture-fit assessment", "Replacement guarantee", "End-to-end hiring support"],
    color: "var(--sb-blue)",
  },
  {
    icon: Briefcase,
    title: "Contract Staffing",
    desc: "Flexible workforce solutions for project-based, seasonal, or temporary hiring needs. We handle compliance, payroll, and administrative overheads so you can focus on business.",
    benefits: ["Scalable workforce on demand", "Full compliance management", "Payroll and statutory handling", "Quick deployment"],
    color: "var(--sb-green)",
  },
  {
    icon: Search,
    title: "Executive Search",
    desc: "Specialized headhunting for C-suite, VP-level, and senior leadership roles. Our discreet, research-driven approach identifies leaders who drive organizational transformation.",
    benefits: ["Confidential search process", "Board-level network access", "Leadership assessment tools", "Succession planning support"],
    color: "var(--sb-orange)",
  },
  {
    icon: Building2,
    title: "Bulk Recruitment",
    desc: "Large-scale hiring campaigns for manufacturing, retail, BPO, and infrastructure sectors. We handle volume without compromising quality, delivering hundreds of hires within tight timelines.",
    benefits: ["Mass screening infrastructure", "Multi-location drives", "Walk-in and campus hiring", "Rapid onboarding pipelines"],
    color: "var(--sb-blue)",
  },
  {
    icon: Handshake,
    title: "HR Consulting",
    desc: "Strategic advisory on HR policy design, organizational restructuring, compliance frameworks, and talent management strategies. We help build HR functions from scratch or optimize existing ones.",
    benefits: ["HR policy development", "Org design & restructuring", "Compensation benchmarking", "Performance management setup"],
    color: "var(--sb-green)",
  },
  {
    icon: Shield,
    title: "Payroll Management",
    desc: "End-to-end payroll processing and management covering salary disbursement, tax compliance, PF/ESI, and all statutory requirements. Error-free, on-time, every month.",
    benefits: ["Accurate salary processing", "Statutory compliance (PF, ESI, PT)", "Tax computation & TDS", "Payslip generation & distribution"],
    color: "var(--sb-orange)",
  },
];

export default function ServicesPage() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-[var(--sb-blue)] text-white py-20 md:py-28">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <ScrollReveal>
            <span className="text-sm font-bold tracking-[0.15em] uppercase text-[var(--sb-orange)] mb-4 block">What We Offer</span>
            <h1 className="font-heading text-5xl md:text-6xl lg:text-7xl font-bold tracking-tight uppercase mb-6">Our Services</h1>
            <p className="text-blue-200 text-base md:text-lg max-w-2xl leading-relaxed">
              Comprehensive HR and manpower solutions designed to help businesses scale and professionals succeed.
            </p>
          </ScrollReveal>
        </div>
      </section>

      {/* Services */}
      <section data-testid="services-detail-section" className="py-20 md:py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <div className="space-y-16">
            {servicesData.map((svc, i) => (
              <ScrollReveal key={svc.title}>
                <div data-testid={`service-detail-${i}`} className={`grid lg:grid-cols-2 gap-8 items-start ${i % 2 === 1 ? "lg:direction-rtl" : ""}`}>
                  <div className={i % 2 === 1 ? "lg:order-2" : ""}>
                    <div className="w-14 h-14 flex items-center justify-center mb-6 text-white" style={{ backgroundColor: svc.color }}>
                      <svc.icon className="w-7 h-7" />
                    </div>
                    <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 uppercase mb-4">{svc.title}</h2>
                    <p className="text-slate-600 text-base leading-relaxed mb-6">{svc.desc}</p>
                    <Link
                      to="/contact"
                      className="text-sm font-bold uppercase tracking-wider inline-flex items-center gap-2 hover:-translate-x-0 hover:translate-x-1 transition-transform"
                      style={{ color: svc.color }}
                    >
                      Enquire Now <ArrowRight className="w-4 h-4" />
                    </Link>
                  </div>
                  <div className={`bg-slate-50 border border-slate-200 p-8 ${i % 2 === 1 ? "lg:order-1" : ""}`}>
                    <h4 className="font-heading text-lg font-bold text-slate-900 uppercase mb-4">Key Benefits</h4>
                    <ul className="space-y-3">
                      {svc.benefits.map((b) => (
                        <li key={b} className="flex items-start gap-3 text-slate-600 text-sm">
                          <CheckCircle2 className="w-5 h-5 flex-shrink-0 mt-0.5" style={{ color: svc.color }} />
                          {b}
                        </li>
                      ))}
                    </ul>
                  </div>
                </div>
                {i < servicesData.length - 1 && <div className="border-b border-slate-200 mt-16" />}
              </ScrollReveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 md:py-28 bg-[var(--sb-blue)] text-white text-center">
        <div className="max-w-3xl mx-auto px-6">
          <ScrollReveal>
            <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight uppercase mb-6">Need a Custom Solution?</h2>
            <p className="text-blue-200 text-base md:text-lg mb-8">Every organization is unique. Let's discuss your specific requirements and build a tailored hiring strategy.</p>
            <Link to="/contact" data-testid="services-contact-cta" className="bg-[var(--sb-orange)] text-white px-8 py-4 text-base font-bold uppercase tracking-wider hover:bg-[#C9661A] transition-all hover:-translate-y-1 inline-flex items-center gap-2">
              Get in Touch <ArrowRight className="w-5 h-5" />
            </Link>
          </ScrollReveal>
        </div>
      </section>
    </div>
  );
}
